import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Alert } from '@orobix/orobix-fe-components'
import { formatDistance } from 'date-fns'
import i18next from 'i18next'
import { useTranslation } from 'react-i18next'

import { DataSyncStatus } from '../../../types/dataSync'
import { formatDateTime } from '../../../utils/date'
import { useStyles } from './IngestionStatusBanner.styles'
import { locales } from './locales'
import { NextCheckLine } from './NextCheckLine'

/**
 * Something is wrong with the synchronization. Two shapes of the same state:
 * a run that happened and failed, and — reported by the backend under its own
 * category — no run at all.
 *
 * The duration replaced the count of consecutive attempts: "4 attempts" only
 * means something to a reader who knows the schedule by heart, and the
 * interval is an environment variable. How long it has been broken is the
 * same fact in a unit everyone reads.
 */
export const FailedBanner = ({ sync }: { sync: DataSyncStatus }) => {
  const classes = useStyles()
  const { t } = useTranslation()

  return (
    <Alert type="danger" withBorder>
      <span className={classes.icon}>
        <FontAwesomeIcon icon={faTriangleExclamation} />
      </span>

      <span>
        <strong>{t(`sync_error_${sync.errorCategory ?? 'unknown'}`)}</strong>

        {/*
          Kept for every category, not only the ones where it reads well: for
          `unknown` the headline says nothing at all and this line is the only
          clue there is. Secondary styling keeps a raw exception message from
          competing with the headline.
        */}
        {sync.errorDetail && (
          <>
            <br />
            <span className={classes.detail}>{sync.errorDetail}</span>
          </>
        )}

        {sync.dataReceivedAt && (
          <>
            <br />
            {t('Last data received on {{ receivedAt }}', {
              receivedAt: formatDateTime(new Date(sync.dataReceivedAt)),
            })}
          </>
        )}

        <NextCheckLine
          nextCheckAt={sync.nextCheckAt}
          stalled={sync.errorCategory === 'scheduler_stalled'}
        />

        {/*
          Nothing to measure from on an installation where no run has ever
          succeeded: the failure has no "since" to report.
        */}
        {sync.lastSuccessAt && (
          <>
            <br />
            {t('Not synchronizing for {{ duration }}', {
              duration: formatDistance(
                new Date(sync.lastSuccessAt),
                new Date(),
                {
                  locale: locales[i18next.language as 'en' | 'it'],
                }
              ),
            })}
          </>
        )}
      </span>
    </Alert>
  )
}
